(() => {
  if (customElements.get('ots-name-your-price')) return;

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  class OtsNameYourPrice extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.openButton = this.querySelector('[data-offer-open]');
      this.dialog = this.querySelector('[data-offer-dialog]');
      this.form = this.querySelector('[data-offer-form]');
      this.formState = this.querySelector('[data-offer-form-state]');
      this.buyNowState = this.querySelector('[data-offer-buy-now-state]');
      this.successState = this.querySelector('[data-offer-success-state]');
      this.status = this.querySelector('[data-offer-status]');
      this.submitButton = this.querySelector('[data-offer-submit]');
      this.lastFocusedElement = null;
      this.lastSubmissionAt = 0;
      this.submissionId = null;
      this.variantChangeUnsubscriber = null;

      if (!this.openButton || !this.dialog || !this.form) return;

      this.openButton.addEventListener('click', () => this.open());
      this.querySelectorAll('[data-offer-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('keydown', (event) => this.handleDialogKeydown(event));
      this.dialog.addEventListener('close', () => this.afterClose());
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.querySelector('[data-offer-try-again]')?.addEventListener('click', () => this.showForm());
      this.querySelectorAll('[data-offer-buy-now]').forEach((link) => {
        link.addEventListener('click', (event) => this.buyNow(event));
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) =>
          this.handleVariantChange(event),
        );
      }
    }

    disconnectedCallback() {
      this.variantChangeUnsubscriber?.();
      if (this.dialog?.open) {
        this.dialog.close();
        document.body.classList.remove('ots-offer-modal-open');
      }
    }

    handleVariantChange(event) {
      if (String(event?.data?.sectionId) !== String(this.closest('product-info')?.dataset.section)) return;

      const sourceOffer = event.data.html?.querySelector('ots-name-your-price');
      if (!sourceOffer) {
        this.hidden = true;
        if (this.dialog.open) this.close();
        return;
      }

      // The server-rendered section contains the authoritative selected variant
      // presentation (formatted price, availability and cart permalink). Replacing
      // this element prevents stale variant copy or CTAs from surviving a change.
      this.replaceWith(sourceOffer);
    }

    open() {
      this.lastFocusedElement = document.activeElement;
      this.dialog.showModal();
      document.body.classList.add('ots-offer-modal-open');
      this.track('offer_modal_opened');
      requestAnimationFrame(() => {
        const target =
          this.dialog.querySelector('[data-offer-success-state]:not([hidden]) [tabindex="-1"]') ||
          this.dialog.querySelector('[data-offer-buy-now-state]:not([hidden]) [tabindex="-1"]') ||
          (!this.formState.hidden && this.form.querySelector('input:not([type="hidden"])')) ||
          this.dialog.querySelector('[data-offer-close]');
        target?.focus();
      });
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    afterClose() {
      document.body.classList.remove('ots-offer-modal-open');
      if (this.lastFocusedElement instanceof HTMLElement && document.contains(this.lastFocusedElement)) {
        this.lastFocusedElement.focus();
      }
    }

    handleDialogKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = [...this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
        (element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.offsetParent !== null,
      );
      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    currentVariantId() {
      return (
        this.closest('product-info')?.querySelector('form[data-type="add-to-cart-form"] .product-variant-id')?.value ||
        this.dataset.variantId
      );
    }

    buyNow(event) {
      const variantId = this.currentVariantId();
      if (!variantId) {
        event.preventDefault();
        return;
      }
      const cartUrl = (this.dataset.cartUrl || '/cart').replace(/\/$/, '');
      event.currentTarget.href = `${cartUrl}/${encodeURIComponent(variantId)}:1?checkout`;
      this.track('offer_buy_now_clicked', {
        context: event.currentTarget.dataset.offerBuyNowContext || 'offer_modal',
      });
    }

    clearErrors() {
      this.form.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));
      this.form.querySelectorAll('[data-error-for]').forEach((error) => {
        error.textContent = '';
        error.hidden = true;
      });
      this.status.textContent = '';
    }

    setFieldError(name, message) {
      const field = this.form.elements.namedItem(name);
      const error = this.form.querySelector(`[data-error-for="${CSS.escape(name)}"]`);
      if (field instanceof HTMLElement) field.setAttribute('aria-invalid', 'true');
      if (error) {
        error.textContent = String(message);
        error.hidden = false;
      }
    }

    parseAmount(value) {
      const normalized = String(value).trim().replace(/^\$/, '').replaceAll(',', '').trim();
      const fractionDigits = this.currencyFractionDigits();
      const amountPattern = fractionDigits === 0
        ? /^\d+$/
        : new RegExp(`^\\d+(?:\\.\\d{1,${fractionDigits}})?$`);
      if (!amountPattern.test(normalized)) return null;

      const [whole, fraction = ''] = normalized.split('.');
      const normalizedWhole = BigInt(whole).toString();
      const normalizedFraction = fraction.padEnd(fractionDigits, '0');
      const minorUnits = (BigInt(normalizedWhole) * (10n ** BigInt(fractionDigits)))
        + BigInt(normalizedFraction || '0');
      if (minorUnits <= 0n) return null;

      return {
        value: fractionDigits === 0
          ? normalizedWhole
          : `${normalizedWhole}.${normalizedFraction}`,
      };
    }

    currencyFractionDigits() {
      const currency = String(this.dataset.currency || '').toUpperCase();
      try {
        const digits = new Intl.NumberFormat('en', { style: 'currency', currency })
          .resolvedOptions().maximumFractionDigits;
        return Number.isInteger(digits) && digits >= 0 && digits <= 6 ? digits : 2;
      } catch (_error) {
        return 2;
      }
    }

    validate() {
      this.clearErrors();
      const amount = this.parseAmount(this.form.elements.offerAmount.value);
      const name = this.form.elements.name.value.trim();
      const email = this.form.elements.email.value.trim();
      const note = this.form.elements.note.value.trim();
      let valid = true;

      if (!amount) {
        const fractionDigits = this.currencyFractionDigits();
        const precision = fractionDigits === 0
          ? 'using whole numbers'
          : `using no more than ${fractionDigits} decimal place${fractionDigits === 1 ? '' : 's'}`;
        this.setFieldError('offerAmount', `Enter a valid amount greater than zero, ${precision}.`);
        valid = false;
      }
      if (!name) {
        this.setFieldError('name', 'Enter your name.');
        valid = false;
      }
      if (!EMAIL_PATTERN.test(email) || email.length > 254) {
        this.setFieldError('email', 'Enter a valid email address.');
        valid = false;
      }
      if (note.length > 2000) {
        this.setFieldError('note', 'Keep your note to 2,000 characters or fewer.');
        valid = false;
      }

      if (!valid) {
        this.status.textContent = 'Please correct the highlighted fields.';
        this.form.querySelector('[aria-invalid="true"]')?.focus();
        this.track('offer_validation_failed', { reason: 'client_validation' });
        return null;
      }

      return { amount, name, email, note };
    }

    async submit(event) {
      event.preventDefault();
      const values = this.validate();
      if (!values) return;

      const now = Date.now();
      if (now - this.lastSubmissionAt < 2000) return;
      this.lastSubmissionAt = now;
      this.setBusy(true);

      const payload = {
        productId: this.dataset.productId,
        variantId: this.currentVariantId(),
        offerAmount: values.amount.value,
        currency: this.dataset.currency,
        name: values.name,
        email: values.email,
        note: values.note,
        honeypot: this.form.elements.honeypot.value,
        submissionId: this.submissionId || (this.submissionId = this.createSubmissionId()),
      };

      try {
        const response = await fetch('/apps/ots-offers/offers', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });
        const result = await response.json().catch(() => ({}));

        if (result.code === 'OFFER_AT_OR_ABOVE_ASKING') {
          this.updateAuthoritativePrice(result);
          this.track('offer_validation_failed', { reason: 'at_or_above_asking' });
          this.showBuyNow();
          return;
        }

        if (!response.ok || result.ok !== true) {
          this.submissionId = null;
          this.applyServerErrors(result, response.status);
          return;
        }

        this.updateAuthoritativePrice(result);
        this.track('offer_submitted');
        this.form.reset();
        this.submissionId = null;
        this.formState.hidden = true;
        this.buyNowState.hidden = true;
        this.successState.hidden = false;
        this.querySelector('[data-offer-success-heading]')?.focus();
      } catch (_error) {
        this.status.textContent = 'We could not submit your offer right now. Please wait a moment and try again.';
        this.track('offer_validation_failed', { reason: 'network_error' });
      } finally {
        this.setBusy(false);
      }
    }

    applyServerErrors(result, statusCode) {
      this.clearErrors();
      if (result.fieldErrors && typeof result.fieldErrors === 'object') {
        Object.entries(result.fieldErrors).forEach(([field, message]) => {
          if (['offerAmount', 'name', 'email', 'note'].includes(field)) {
            this.setFieldError(field, Array.isArray(message) ? message[0] : message);
          }
        });
      }
      const policyMessages = {
        OFFER_REQUEST_UNAVAILABLE: 'We couldn’t open a new offer for this item with that email. Check the inbox for that address for an existing offer update, or buy at the listed price.',
      };
      const defaultMessage = statusCode === 429
        ? 'Too many requests were sent. Please wait a little while before trying again. No offer attempt was used.'
        : 'We could not submit this offer. Review your information and try again.';
      const safeServerMessage = typeof result.message === 'string' ? result.message : '';
      this.status.textContent = policyMessages[result.code] || (statusCode === 429 ? defaultMessage : safeServerMessage || defaultMessage);
      this.form.querySelector('[aria-invalid="true"]')?.focus();
      this.track('offer_validation_failed', { reason: result.code || `server_${statusCode}` });
    }

    updateAuthoritativePrice(result) {
      const rawPrice = result.currentAskingPrice ?? result.currentPrice;
      const currency = String(result.currency || this.dataset.currency || '').toUpperCase();
      let formattedPrice = typeof result.currentPriceFormatted === 'string' ? result.currentPriceFormatted.trim() : '';

      if (!formattedPrice && rawPrice !== undefined && /^[A-Z]{3}$/.test(currency)) {
        const numericPrice = Number(rawPrice);
        if (Number.isFinite(numericPrice) && numericPrice >= 0) {
          try {
            formattedPrice = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
              style: 'currency',
              currency,
              maximumFractionDigits: Number.isInteger(numericPrice) ? 0 : 2,
            }).format(numericPrice);
          } catch (_error) {
            formattedPrice = '';
          }
        }
      }

      if (!formattedPrice) return;
      this.querySelectorAll('[data-offer-current-price]').forEach((element) => {
        element.textContent = formattedPrice;
      });
      if (currency) this.dataset.currency = currency;
    }

    showBuyNow() {
      this.formState.hidden = true;
      this.successState.hidden = true;
      this.buyNowState.hidden = false;
      this.querySelector('[data-offer-buy-now-heading]')?.focus();
    }

    showForm() {
      this.buyNowState.hidden = true;
      this.successState.hidden = true;
      this.formState.hidden = false;
      this.clearErrors();
      this.form.elements.offerAmount.focus();
    }

    setBusy(isBusy) {
      this.submitButton.disabled = isBusy;
      this.submitButton.classList.toggle('is-loading', isBusy);
      this.form.setAttribute('aria-busy', String(isBusy));
      this.status.textContent = isBusy ? 'Submitting your offer...' : this.status.textContent;
    }

    createSubmissionId() {
      if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }

    track(eventName, details = {}) {
      const payload = {
        productId: this.dataset.productId,
        variantId: this.currentVariantId(),
        currency: this.dataset.currency,
        source: 'ots_name_your_price',
        ...details,
      };
      try {
        window.Shopify?.analytics?.publish?.(eventName, payload);
      } catch (_error) {
        // Analytics must never interrupt the offer experience.
      }
      window.dispatchEvent(new CustomEvent('ots:analytics', { detail: { event: eventName, ...payload } }));
    }
  }

  customElements.define('ots-name-your-price', OtsNameYourPrice);
})();
